/**
 * Resolves program creator label + avatar for UI cards.
 *
 * **Member programs** (`GET .../groups/{orgId}/member/programs`, `groupDetails[0]`) — aggregate
 * aligned with `getGroupDetails` / `getProgramListForMember` exposes:
 *   `creator.id`, `creator.fullName`, `creator.profileImage`, `creator.email`
 * (`fullName` = firstName + lastName when available, else user `name`; `profileImage` = headshot URL or "").
 *
 * **Admin list** (`getAdminProgramList`) still often sends `creator.name`, `creator.headshot`, `createdBy`.
 *
 * This helper prefers the member-program `creator` fields, then falls back to admin/legacy shapes
 * and nested `createdBy` user objects.
 */

const formatCreatorDisplayName = (value: string) => {
  if (!value) return 'Program admin';
  if (value.includes('@')) {
    return value.split('@')[0] || 'Program admin';
  }
  return value;
};

const isLikelyMongoObjectId = (value: string) => /^[a-f0-9]{24}$/i.test(value.trim());

const mergeProgramCreatorPerson = (details: any) => {
  if (!details) return { merged: {} as Record<string, unknown>, createdByString: '' };
  const creatorObj =
    details.creator && typeof details.creator === 'object' && !Array.isArray(details.creator) ? details.creator : {};
  const createdByField = details.createdBy;
  let createdByString = '';
  if (typeof createdByField === 'string') {
    createdByString = createdByField;
  }
  const createdByObj =
    createdByField && typeof createdByField === 'object' && !Array.isArray(createdByField) ? createdByField : {};
  const nestedUser = createdByObj.user && typeof createdByObj.user === 'object' ? createdByObj.user : {};
  const nestedUserDetails =
    createdByObj.userDetails && typeof createdByObj.userDetails === 'object' && !Array.isArray(createdByObj.userDetails)
      ? createdByObj.userDetails
      : {};
  const personal =
    (nestedUserDetails.personal_details as Record<string, unknown> | undefined) ||
    (createdByObj.personal_details as Record<string, unknown> | undefined) ||
    {};
  const merged = {
    ...personal,
    ...nestedUserDetails,
    ...nestedUser,
    ...createdByObj,
    ...creatorObj
  };
  return { merged, createdByString };
};

const pickProfileImageUrl = (source: any, fallback?: any): string | undefined => {
  const candidates = [
    source?.headshot,
    source?.headShot,
    source?.image,
    source?.imageUrl,
    source?.photoUrl,
    source?.profileImage,
    source?.avatar,
    source?.photo,
    source?.picture,
    source?.displayPicture,
    source?.profilePhoto,
    source?.profilePicture,
    source?.profilePic,
    source?.personal_details?.headshot,
    source?.personal_details?.image,
    source?.userDetails?.headshot,
    source?.userDetails?.image,
    source?.userDetails?.personal_details?.headshot,
    source?.userDetails?.personal_details?.image,
    source?.user?.headshot,
    source?.user?.headShot,
    source?.user?.image,
    source?.user?.photo,
    source?.user?.picture,
    source?.extended?.headshot,
    source?.extended?.headShot,
    source?.extended?.image,
    source?.extended?.photo,
    source?.extended?.picture,
    fallback?.headshot,
    fallback?.headShot,
    fallback?.image,
    fallback?.avatar,
    fallback?.photo,
    fallback?.picture,
    fallback?.displayPicture,
    fallback?.creatorHeadshot,
    fallback?.creatorAvatar,
    fallback?.userDetails?.headshot,
    fallback?.userDetails?.image,
    fallback?.userDetails?.personal_details?.headshot,
    fallback?.userDetails?.personal_details?.image
  ];
  const image = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
  if (!image || image.includes('placehold.co')) return undefined;
  return image;
};

const resolveCreatorName = (details: any, merged: Record<string, unknown>, createdByString: string): string => {
  const creator =
    details?.creator && typeof details.creator === 'object' && !Array.isArray(details.creator) ? details.creator : {};
  const fullName = typeof creator.fullName === 'string' ? creator.fullName.trim() : '';
  if (fullName) {
    return formatCreatorDisplayName(fullName);
  }
  const cFirst = typeof creator.firstName === 'string' ? creator.firstName.trim() : '';
  const cLast = typeof creator.lastName === 'string' ? creator.lastName.trim() : '';
  if (cFirst || cLast) {
    return [cFirst, cLast].filter(Boolean).join(' ');
  }
  const cName = typeof creator.name === 'string' ? creator.name.trim() : '';
  if (cName) {
    return formatCreatorDisplayName(cName);
  }
  if (createdByString.trim()) {
    if (!isLikelyMongoObjectId(createdByString)) {
      return formatCreatorDisplayName(createdByString.trim());
    }
  }
  const mFirst = typeof merged.firstName === 'string' ? merged.firstName.trim() : '';
  const mLast = typeof merged.lastName === 'string' ? merged.lastName.trim() : '';
  if (mFirst || mLast) {
    return [mFirst, mLast].filter(Boolean).join(' ');
  }
  const display =
    (typeof merged.displayName === 'string' && merged.displayName.trim()) ||
    (typeof merged.name === 'string' && merged.name.trim()) ||
    '';
  if (display) return formatCreatorDisplayName(display);
  const fallbacks = [details?.creatorName, details?.createdByName, details?.adminName].filter(Boolean) as string[];
  for (const raw of fallbacks) {
    if (raw && String(raw).trim()) return formatCreatorDisplayName(String(raw));
  }
  return 'Program admin';
};

const resolveCreatorAvatar = (details: any, merged: Record<string, unknown>): string | undefined => {
  const creator =
    details?.creator && typeof details.creator === 'object' && !Array.isArray(details.creator) ? details.creator : {};
  const profileImage = typeof creator.profileImage === 'string' ? creator.profileImage.trim() : '';
  if (profileImage && !profileImage.includes('placehold.co')) {
    return profileImage;
  }
  const fromCreator = pickProfileImageUrl(creator, null);
  if (fromCreator) return fromCreator;

  const mergedForImage = {
    ...merged,
    headshot:
      merged.headshot ||
      merged.headShot ||
      details?.creatorHeadshot ||
      details?.createdByHeadshot ||
      details?.headshot,
    picture: merged.picture || details?.creatorAvatar || details?.createdByPicture || details?.picture
  };
  return pickProfileImageUrl(mergedForImage, details);
};

export type ProgramCreatorDisplay = { name: string; avatar?: string };

/**
 * @param details — `groupDetails[0]` from member programs API, or a row from `getAdminProgramList`
 */
export function getProgramCreatorFromGroupOrProgramDetails(details: any): ProgramCreatorDisplay {
  if (!details) {
    return { name: 'Program admin' };
  }
  const { merged, createdByString } = mergeProgramCreatorPerson(details);
  const name = resolveCreatorName(details, merged, createdByString);
  const avatar = resolveCreatorAvatar(details, merged);
  return { name, avatar };
}
